import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UserRegistration {
  id: string;
  category: string | null;
  subcategory: string | null;
  modality: string | null;
  partner_name: string | null;
  payment_status: string | null;
}

export const useUserRegistration = (eventId: string) => {
  const [registrations, setRegistrations] = useState<UserRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        // Get all user's registrations for this event - search both user_id AND partner_user_id
        const { data: registrationData, error: regError } = await supabase
          .from('registrations')
          .select('id, category, subcategory, modality, user_id, partner_user_id, payment_status, player1_name, player2_name')
          .eq('event_id', eventId)
          .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`)
          .order('registered_at', { ascending: false });

        if (regError) throw regError;

        if (!registrationData || registrationData.length === 0) {
          setRegistrations([]);
          setLoading(false);
          return;
        }

        // Process each registration
        const processedRegistrations: UserRegistration[] = [];

        for (const reg of registrationData) {
          // Determine user's role and get partner name accordingly
          const isPlayer1 = reg.user_id === user.id;
          let partnerName = null;

          if (isPlayer1) {
            // User is player1, partner is player2
            // Priority: player2_name (manual entry) > profiles lookup
            if (reg.player2_name) {
              partnerName = reg.player2_name;
            } else if (reg.partner_user_id) {
              const { data: partnerData } = await supabase
                .from('profiles')
                .select('full_name')
                .eq('user_id', reg.partner_user_id)
                .maybeSingle();
              partnerName = partnerData?.full_name || null;
            }
          } else {
            // User is player2, partner is player1
            // Priority: player1_name (manual entry) > profiles lookup
            if (reg.player1_name) {
              partnerName = reg.player1_name;
            } else if (reg.user_id) {
              const { data: partnerData } = await supabase
                .from('profiles')
                .select('full_name')
                .eq('user_id', reg.user_id)
                .maybeSingle();
              partnerName = partnerData?.full_name || null;
            }
          }

          console.log('[useUserRegistration] Found registration:', {
            registrationId: reg.id,
            eventId,
            userId: user.id,
            isPlayer1,
            category: reg.category,
            subcategory: reg.subcategory,
            player1_name: reg.player1_name,
            player2_name: reg.player2_name,
            partnerName
          });

          processedRegistrations.push({
            id: reg.id,
            category: reg.category,
            subcategory: reg.subcategory,
            modality: reg.modality,
            partner_name: partnerName,
            payment_status: reg.payment_status,
          });
        }

        setRegistrations(processedRegistrations);

      } catch (err) {
        console.error('Error fetching user registrations:', err);
        setError(err instanceof Error ? err.message : 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchRegistrations();
    }
  }, [eventId]);

  // For backward compatibility, also export the first registration as 'registration'
  // Always return an array (never null) to avoid null reference errors
  const registration = registrations.length > 0 ? registrations[0] : null;

  return { registration, registrations, loading, error };
};
